import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopContext = createContext(null);

export const useShop = () => useContext(ShopContext);

export const ShopProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [restocks, setRestocks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProducts = useCallback(async () => {
    const res = await axios.get(`${API}/products`);
    setProducts(res.data);
    return res.data;
  }, []);

  const loadTransactions = useCallback(async () => {
    const res = await axios.get(`${API}/transactions`);
    setTransactions(res.data);
  }, []);

  const loadRestocks = useCallback(async () => {
    const res = await axios.get(`${API}/restocks`);
    setRestocks(res.data);
  }, []);

  const loadSummary = useCallback(async () => {
    const res = await axios.get(`${API}/reports/summary`);
    setSummary(res.data);
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadProducts(), loadTransactions(), loadRestocks(), loadSummary()]);
  }, [loadProducts, loadTransactions, loadRestocks, loadSummary]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const prods = await loadProducts();
        if (prods.length === 0) {
          await axios.post(`${API}/seed`);
        }
        await refreshAll();
      } catch (e) {
        toast.error("Gagal memuat data dari server");
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addProduct = async (payload) => {
    await axios.post(`${API}/products`, payload);
    await Promise.all([loadProducts(), loadSummary()]);
    toast.success("Barang berhasil ditambahkan");
  };

  const updateProduct = async (id, payload) => {
    await axios.put(`${API}/products/${id}`, payload);
    await Promise.all([loadProducts(), loadSummary()]);
    toast.success("Barang berhasil diperbarui");
  };

  const deleteProduct = async (id) => {
    await axios.delete(`${API}/products/${id}`);
    await Promise.all([loadProducts(), loadSummary()]);
    toast.success("Barang dihapus");
  };

  const checkout = async (items, bayar) => {
    const res = await axios.post(`${API}/transactions`, { items, bayar });
    await refreshAll();
    return res.data;
  };

  const addRestock = async (payload) => {
    const res = await axios.post(`${API}/restocks`, payload);
    await Promise.all([loadProducts(), loadRestocks(), loadSummary()]);
    return res.data;
  };

  const updateRestock = async (id, payload) => {
    const res = await axios.put(`${API}/restocks/${id}`, payload);
    await Promise.all([loadProducts(), loadRestocks(), loadSummary()]);
    return res.data;
  };

  const deleteRestock = async (id) => {
    await axios.delete(`${API}/restocks/${id}`);
    await Promise.all([loadProducts(), loadRestocks(), loadSummary()]);
  };

  return (
    <ShopContext.Provider
      value={{
        products,
        transactions,
        restocks,
        summary,
        loading,
        refreshAll,
        addProduct,
        updateProduct,
        deleteProduct,
        checkout,
        addRestock,
        updateRestock,
        deleteRestock,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};
